import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, ArrowUpRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import ColorSwatches from "./ColorSwatches";

export default function ProductCard({ product }) {
  const { add, toggleWish, inWish } = useCart();
  const navigate = useNavigate();
  const hasColors = product.variants?.length > 0;
  const off = product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : 0;

  return (
    <div className="card-luxury group flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block relative">
        <div className="relative overflow-hidden aspect-[4/5]" style={{ background: "linear-gradient(180deg, #F7F2E7 0%, #EFE9D9 100%)" }}>
          {/* Ambient vignette */}
          <div className="absolute inset-0 pointer-events-none z-[1]" style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(30,27,21,0.08) 100%)" }}></div>
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.06] mix-blend-multiply"
            loading="lazy"
          />

          {/* Discount pill — brass foil */}
          {off > 0 && (
            <span
              className="absolute top-4 left-4 text-[9px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full z-[2]"
              style={{
                background: "linear-gradient(180deg, #B08616, #5C4708)",
                color: "#FDFBF6",
                boxShadow: "0 1px 0 rgba(255,240,200,0.35) inset, 0 6px 14px -4px rgba(138,106,15,0.4)",
              }}
              data-testid={`product-discount-${product.id}`}
            >
              −{off}%
            </span>
          )}

          {/* Bestseller — editorial black plaque */}
          {product.featured && (
            <span className="absolute top-4 right-4 text-[9px] font-semibold uppercase tracking-[0.22em] px-3 py-1.5 z-[2]"
              style={{
                background: "#0D0C0A",
                color: "#E8D68C",
                fontFamily: "'JetBrains Mono', monospace",
              }}>
              Bestseller
            </span>
          )}

          {/* Wishlist */}
          <button
            onClick={(e) => { e.preventDefault(); toggleWish(product); }}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition z-[2]"
            style={{
              background: "rgba(253, 251, 246, 0.95)",
              border: "1px solid rgba(15, 12, 8, 0.12)",
              boxShadow: "0 4px 12px rgba(30, 27, 21, 0.08)",
              backdropFilter: "blur(6px)",
            }}
            data-testid={`wish-${product.id}`}
            aria-label="Wishlist"
          >
            <Heart className={`w-4 h-4 transition ${inWish(product.id) ? "fill-current" : ""}`} style={{ color: inWish(product.id) ? "#8A6A0F" : "#0D0C0A" }} />
          </button>

          {/* Quick view label — appears on hover */}
          <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0 z-[2]">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" style={{ background: "rgba(13,12,10,0.9)", backdropFilter: "blur(6px)" }}>
              Quick view <ArrowUpRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>

      <div className="p-6 flex flex-col gap-2 flex-1" style={{ background: "var(--surface-elev)" }}>
        <div className="flex items-center gap-2 num-badge text-[9px]">
          <span>{product.brand}</span>
          {product.category && <span className="opacity-40">·</span>}
          {product.category && <span className="opacity-70">{product.category}</span>}
        </div>
        <Link to={`/product/${product.id}`} className="font-display text-2xl leading-[1.15] transition line-clamp-2 min-h-[56px] tracking-[-0.01em]" style={{ color: "var(--ink)" }}>
          <span className="group-hover:brass-text transition">{product.name}</span>
        </Link>

        {product.variants?.length > 0 && (
          <ColorSwatches variants={product.variants} size="sm" testPrefix={`card-swatch-${product.id}`} />
        )}

        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-tight" style={{ color: "var(--ink)" }}>₹{product.price?.toLocaleString('en-IN')}</span>
          {product.original_price && (
            <span className="text-xs line-through" style={{ color: "rgba(15,12,8,0.4)" }}>₹{product.original_price?.toLocaleString('en-IN')}</span>
          )}
          <span className="text-[10px] ml-auto" style={{ color: "rgba(15,12,8,0.42)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.05em" }}>Incl. taxes</span>
        </div>

        <div className="mt-3 hairline"></div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => (hasColors ? navigate(`/product/${product.id}`) : add(product))}
            className="text-[11px] font-semibold uppercase tracking-[0.16em] py-3 rounded-full transition flex items-center justify-center gap-1.5 hover:bg-black hover:text-white"
            style={{ border: "1px solid rgba(15, 12, 8, 0.18)" }}
            data-testid={`add-cart-${product.id}`}
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Add
          </button>
          <Link
            to={`/product/${product.id}`}
            className="btn-gold text-[11px] py-3 tracking-[0.16em] uppercase"
            style={{ padding: "0.75rem 1rem" }}
            data-testid={`buy-now-${product.id}`}
          >
            Buy Now
          </Link>
        </div>
      </div>
    </div>
  );
}
